import React, { useContext, useState } from "react";
import { Box, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { AuthContext } from "../../context/auth";
import { TranslationsContext } from "../../context/translations";
import { buildAppUrl, getCookie } from "../../App";
import RedirectingScreen from "../../components/RedirectingScreen";
import { useHistory } from "react-router-dom";

const APP_DESCRIPTIONS: Record<string, string> = {
  mmaz: "Manage your account, profile, and preferences",
  mmdz: "Distribute your music to digital platforms worldwide",
  mmpz: "Manage your publishing rights and royalties",
};

const APP_ACCENT: Record<string, string> = {
  mmaz: "#3399FE",
  mmdz: "#10b981",
  mmpz: "#f59e0b",
};

interface AppDef {
  key: string;
  logo: string;
  name: string;
  linkUrl: string;
  internal?: boolean;
}

interface AppCardProps {
  app: AppDef;
  index: number;
  onLaunch: (app: AppDef) => void;
}

function AppCard({ app, index, onLaunch }: AppCardProps) {
  const accent = APP_ACCENT[app.key] ?? "#6366f1";
  const description = APP_DESCRIPTIONS[app.key] ?? "";

  return (
    <Box
      onClick={() => onLaunch(app)}
      sx={{
        position: "relative",
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: "16px",
        p: "32px 28px 28px",
        cursor: "pointer",
        overflow: "hidden",
        transition: "transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease, background 0.22s ease",
        opacity: 0,
        animation: `cardFadeIn 0.45s ease forwards`,
        animationDelay: `${index * 0.1}s`,
        "@keyframes cardFadeIn": {
          from: { opacity: 0, transform: "translateY(16px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "2px",
          background: `linear-gradient(90deg, ${accent}cc, ${accent}44)`,
          borderRadius: "16px 16px 0 0",
        },
        "&:hover": {
          background: "rgba(255,255,255,0.055)",
          borderColor: `${accent}35`,
          transform: "translateY(-5px)",
          boxShadow: `0 16px 48px rgba(0,0,0,0.3), 0 0 0 1px ${accent}18`,
        },
        "&:hover .card-arrow": {
          transform: "translateX(3px)",
          opacity: 1,
        },
      }}
    >
      {/* Logo */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <img
          src={app.logo}
          alt={app.name}
          style={{ height: 68, width: "auto", objectFit: "contain" }}
        />
      </Box>

      {/* Name */}
      <Typography
        sx={{
          fontSize: "15px",
          fontWeight: 700,
          color: "rgba(255,255,255,0.88)",
          letterSpacing: "-0.2px",
          mb: "6px",
          textAlign: "center",
        }}
      >
        {app.name}
      </Typography>

      {/* Description */}
      <Typography
        sx={{
          fontSize: "12.5px",
          color: "rgba(255,255,255,0.36)",
          lineHeight: 1.55,
          textAlign: "center",
          mb: 3,
        }}
      >
        {description}
      </Typography>

      {/* Open row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "5px",
          color: accent,
          opacity: 0.75,
        }}
      >
        <Typography sx={{ fontSize: "12.5px", fontWeight: 600, letterSpacing: "0.02em" }}>
          Open
        </Typography>
        <ArrowForwardIcon
          className="card-arrow"
          sx={{
            fontSize: "13px",
            transition: "transform 0.18s ease, opacity 0.18s ease",
            opacity: 0.75,
          }}
        />
      </Box>
    </Box>
  );
}

export default function Dashboard() {
  const [redirectingTo, setRedirectingTo] = useState<{ url: string; appKey: string } | null>(null);
  const history = useHistory();

  const whiteLabel = getCookie(process.env.REACT_APP_WHITE_LABEL_COOKIE);
  const profileCookie = getCookie(process.env.REACT_APP_MCZ_PROFILE_COOKIE);
  const profileTypeCookie = getCookie(process.env.REACT_APP_MCZ_PROFILE_TYPE_COOKIE);

  const { user, profileNameLogin, profileImageURLLogin } = useContext(AuthContext);
  const { translations } = useContext(TranslationsContext);

  document.title = profileNameLogin ? `${profileNameLogin} - Dashboard` : "Dashboard";

  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const firstName = user?.user_profile?.first_name;

  const showMMDZ = (user as any).user_applications.some((a: any) => a?.application === "mmdz");
  const showMMPZ = (user as any).user_applications.some((a: any) => a?.application === "mmpz");

  const mmpzUrl = buildAppUrl(
    "mmpz",
    whiteLabel
      ? "/"
      : `/dashboard?parent=${profileCookie ?? ""}&profile_type=${profileTypeCookie ?? ""}`
  );

  const apps: AppDef[] = [
    {
      key: "mmaz",
      logo: "/MMAZ-logo.png",
      name: translations["title.mmaz"] ?? "My Music Account Zone",
      linkUrl: "/my-account",
      internal: true,
    },
    showMMDZ && {
      key: "mmdz",
      logo: "/MMDZ-logo.png",
      name: translations["title.mmdz"] ?? "My Music Distribution Zone",
      linkUrl: buildAppUrl("mmdz", "/"),
    },
    showMMPZ && {
      key: "mmpz",
      logo: "/MMPZ-logo.png",
      name: translations["title.mmpz"] ?? "My Music Publishing Zone",
      linkUrl: mmpzUrl,
    },
  ].filter(Boolean) as AppDef[];

  const handleLaunch = (app: AppDef) => {
    if (app.internal) {
      history.push(app.linkUrl);
    } else {
      setRedirectingTo({ url: app.linkUrl, appKey: app.key });
    }
  };

  if (redirectingTo) {
    return (
      <RedirectingScreen
        url={redirectingTo.url}
        appKey={redirectingTo.appKey}
        profileName={profileNameLogin}
        logoUrl={profileImageURLLogin}
        cancelLabel="Back to dashboard"
        onCancel={() => setRedirectingTo(null)}
      />
    );
  }

  return (
    <Box
      sx={{
        margin: "-20px",
        minHeight: "calc(100vh - 56px)",
        background: "#0D1017",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 2, sm: 4 },
        py: 6,
      }}
    >
      {/* Greeting */}
      <Box
        sx={{
          textAlign: "center",
          mb: 5,
          opacity: 0,
          animation: "greetFadeIn 0.4s ease forwards",
          "@keyframes greetFadeIn": {
            from: { opacity: 0, transform: "translateY(-8px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: "22px", sm: "26px" },
            fontWeight: 700,
            color: "rgba(255,255,255,0.88)",
            letterSpacing: "-0.4px",
            mb: "6px",
          }}
        >
          {greeting}{firstName ? `, ${firstName}` : ""}
        </Typography>
        <Typography
          sx={{
            fontSize: "14px",
            color: "rgba(255,255,255,0.35)",
            letterSpacing: "0.01em",
          }}
        >
          Select an application to get started
        </Typography>
      </Box>

      {/* App cards grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: apps.length === 1 ? "280px" : "repeat(2, 260px)",
            md: apps.length >= 3 ? "repeat(3, 240px)" : apps.length === 2 ? "repeat(2, 280px)" : "280px",
          },
          gap: "16px",
          width: "100%",
          maxWidth: "820px",
          justifyContent: "center",
        }}
      >
        {apps.map((app, i) => (
          <AppCard key={app.key} app={app} index={i} onLaunch={handleLaunch} />
        ))}
      </Box>
    </Box>
  );
}
